// BasePropertyView.js

import { View } from 'backbone';
import util from 'util/Util';
import eventBus         from 'EventBus';
import userService              from 'UserService';
import themeService from 'ThemeService';
import { pickerCfg } from 'pickerCfg';
import listService from 'ListService';
import  Immutable from 'immutable';
import textUtil from 'util/TextUtil';
import imageUtil from 'util/ImageUtil';
import textConstants from 'TextConstants.js';
import undoService from 'UndoService';
import $ from "jquery";
import constants from "../constants/Constants";

// Use to indentify these properties for common action. Add more here if they arise in maintenance. 
const dropdownPropSet = new Set(['fontFamily', 'fontSize', 'buttonFontFamily', 'buttonFontSize']);

class BasePropertyView extends View {

    initialize(options) {
        this.options = options || {};

        // Update if changes to model. Use {silet: true} if no render is needed (or immediatelly needed)
        this.listenTo(this.model, 'change', this.render);

        // Child class would override, supplying a list of model properties that need a sticky dropdown selection.
        this.dropdownPropNames = Immutable.List([]);

        return this;
    }

    getFocus(ev) {         
        
        $(ev.currentTarget).addClass('pfSelectedBlue');
    }

    lostFocus(ev) {
        $(ev.currentTarget).removeClass('pfSelectedBlue');
        
    }


    isThemeProperty(val) {
        return val && typeof val === 'string' && val.length > 0 && val.charAt(0) === '~';
    }

    resolveThemeFontProperty(property, colModel = undefined) {
        const
            model = colModel || this.model,
            val = model.get(property);

        if (this.isThemeProperty(val)) {
            const propArray = themeService.resolve(val).split(',');
            switch (property) {
                case 'fontFamily':
                case 'titleFontFamily':
                case 'buttonFontFamily':
                    return propArray[0].trim();
                case 'fontSize':
                case 'titleFontSize':
                case 'buttonFontSize':
                    return propArray[1].trim();
                case 'textWeight':
                case 'titleTextWeight':
                case 'buttonTextWeight':
                    return propArray[2] ? propArray[2].trim(): '';
                case 'textStyle':
                case 'titleTextStyle':
                case 'buttonTextStyle':
                    return propArray[3]? propArray[3].trim() : '';
                default:
                    break;
            }
        } else {
            return val;
        }
    }

    resolveThemeHeaderFontProperty(property) {
        let val = this.model.get(property);
        if (this.isThemeProperty(val)) {
            let propArray = themeService.resolve(val).split(',');
            switch (property) {
                case 'headerFontFamily':
                    return propArray[0].trim();
                case 'headerFontSize':
                    return propArray[1].trim();
                case 'headerTextWeight':
                    return propArray[2]? propArray[2].trim(): '';
                case 'headerTextStyle':
                    return propArray[3]? propArray[3].trim() : '';
                default:
                    break;
            }
        } else {
            return val;
        }
    }


    resolveThemeBorderProperty(property) {
        const val = this.model.get(property);
        if (this.isThemeProperty(val)) {
            const propArray = themeService.resolve(val).split(',');
            if (propArray.length > 1) {
                switch (property) {
                    case 'borderSize':
                        return propArray[0].trim();
                    case 'borderStyle':
                        return propArray[1].trim().toLowerCase();
                    case 'cornerRadius':
                        return propArray.length > 2 ? propArray[2].trim() : themeService.resolve(val);
                    default:
                        break;
                }
            } else {
                return themeService.resolve(val)
            }
        } else {
            return val;
        }
    }

    resolveTheme(value) {
        if (this.isThemeProperty(value)) {
            return themeService.resolve(value);
        }

        return value;
    }

    resolveColor(color) {
        return this.resolveTheme(color).toUpperCase();
    }

    // Neighbor aligner uses this polymorphic property (future)
    get kind() { return 'BasePropertyView'; }

    resolveThemeColors(model, properties) {
        let propertiesOfModel = model.toJSON();

        // Scan each properties to check for theme
        properties.forEach((name) => {
            if (!propertiesOfModel[name]) {
                propertiesOfModel[name] = "#FFFFFF";
                // console.log(`resolveThemeColors property "${name}" not found. Defaulting to ${propertiesOfModel[name]}`);
            } else {
                if (this.isThemeProperty(propertiesOfModel[name])) {
                    propertiesOfModel[name] = themeService.resolve(propertiesOfModel[name]);
                }
            }    
        });

        return propertiesOfModel;
    }

    updateCheckboxAttributes(ev) {
        this.updateAttributes(ev);
    }


    // Use for 'change input[type=text]',  and other change events. Handles table column events if columnModel supplied.
    // This method is highly shared, so be careful of breakage.
    updateAttributes(ev, columnModel = undefined, name=undefined, value=undefined) {
        if (!ev) {
            return;
        }
        ev.preventDefault();

        let
            propName = ev.currentTarget.name || name,
            propVal = ev.currentTarget.name? ev.currentTarget.value : value,
            model = columnModel || this.model,
            oldTitleHeight = model.get('titleHeight'),
            oldPropVal=model.get(propName),
            height=model.get('titleHeight');
        
        propName = propName === 'customFontFamily' ? 'fontFamily' : propName === 'customTitleFontFamily' ? 'titleFontFamily' : propName;
        
        if( propName === 'titleFontSize' && model.get('factoryKey') === 'control_group' && model.get('titleText') !== '') {
            const fontFamily = themeService.resolveThemeFont('fontFamily', model.get('titleFontFamily'));
            const height = util.measureTextHeight(model.get('titleText'), fontFamily + ',' + propVal + ',normal,normal', util.extractNum(model.get('width')));

            if (Number(height) > Number(model.get('titleHeight'))) {
                model.set('titleHeight', height, {silent: true});
            }

        } else if (columnModel && dropdownPropSet.has(propName)) {
            // We know it's a column because we have a columnModel.
            // So select table column bound control's font option, etc.
            util.selectDropdownItem( propName, propVal);
        }
       
        const _textControlsIncreaseHeight = new Immutable.Set(['control_button',
                                                               'control_submit_button',
                                                               'control_page_button',
                                                               'control_print_button',
                                                               'control_play_form_button',
                                                               'control_play_report_button',
                                                               'control_clear_filter_button',
                                                               'control_progress_bar',
                                                               'control_file_attachment',
                                                               'control_websignature',
                                                               'control_image_input']);

        if (_textControlsIncreaseHeight.contains(model.get('factoryKey'))){
            propName = propName === '' ? ev.currentTarget.parentElement.parentElement.children[0].name : propName;
            if(propName === 'fontSize' || propName === 'buttonFontSize'){                
                if(model.get('factoryKey') === 'control_websignature' || model.get('factoryKey') === 'control_image_input'){
                    if(this.model.get('minHeight')){
                        this.model.set('minHeight', Number(propVal) * 1.5);    
                    }
                }else{
                    //propVal = util.isEmpty(ev.currentTarget.parentElement) ? ev.currentTarget.value: util.isEmpty(ev.currentTarget.parentElement.parentElement.children[0].value) ? ev.currentTarget.value : ev.currentTarget.parentElement.parentElement.children[0].value;
                    textUtil.updateTextContent(this.model, true, {silent:true});
                }
            }
        }


        if (util.isGroupContainer(model.get('factoryKey'))){
            util.updatePropertyRenderAndSendEvent(propName,propVal,model,this.render.bind(this),'GroupPropertyChange');

        }else{
            model.set(propName, propVal);
        }


        undoService.add({
            undo: () =>{
                model.set('titleHeight',oldTitleHeight,{silent:true});
                if (util.isGroupContainer(model.get('factoryKey'))){
                    util.updatePropertyRenderAndSendEvent(propName,oldPropVal,model,this.render.bind(this),'GroupPropertyChange');
                }else{
                    model.set(propName, oldPropVal);
                }
            },
            redo:() => {
                model.set('titleHeight',height,{silent:true});
                if (model.get('factoryKey') === "control_radio_button_group" ||
                    model.get('factoryKey') === "control_checkbox_group" ||
                    model.get('factoryKey') === "control_group"){
                    util.updatePropertyRenderAndSendEvent(propName,propVal,model,this.render.bind(this),'GroupPropertyChange');

                }else{
                    model.set(propName, propVal);
                }
            }
        },textConstants.UNDO_PROP_CHANGE);
    }

    // Use for 'change input[type=text] which validates a text color
    updateColorAttributesWithValidation(ev) {
        ev.preventDefault();
        const propName = ev.currentTarget.name,
              oldValue = this.model.get(propName),
              propVal = ev.currentTarget.value;

        if (util.isValidColorcode(propVal)) {
            let color = this.resolveColor(propVal);
            this.model.set(propName, color);

            if ( this.isThemeProperty(oldValue) ){
                themeService.deattachTheme(this);
            }

            this.render();
            return true;
        }
        
        // Call render if invalid color
        this.render();
        return false;
    }

    // Use for 'change input[type=color]    
    // Note: id and name of control must be the name of model + suffix Picker
    // Eg: frameColor will be frameColorPicker
    updateColorAttributes(ev) {
        ev.preventDefault();
        const propName = ev.currentTarget.id.replace('Picker', ''),
              oldValue = this.model.get(propName),
              propVal = ev.currentTarget.value;

        let color = this.resolveColor(propVal);

        // Update model
        if (util.isGroupContainer(this.model.get('factoryKey'))){
            util.updatePropertyRenderAndSendEvent(propName,propVal,this.model,this.render.bind(this),'GroupPropertyChange');

        } else if ('layout_frame' === this.model.get('factoryKey') && propName === "outsideColor"){

            if(listService.mainKind === constants.KIND_MOBILE){
                this.model.set('mobileOutsideColor', propVal);
                listService.currentForm.model.set('mobileOutsideColor', propVal);
                listService.currentForm.render();
            }
            else{
                eventBus.trigger('outsideColorSet', color);
            }
        }else{
            this.model.set(propName, color);
        }

        if ( this.isThemeProperty(oldValue) ){
            themeService.deattachTheme(this);
        }

        // Update text input
        $('#' + propName).val(color);

        // PF-3935 Refreshing the image from the server
        if(this.model.get('factoryKey') === 'control_captcha'){
            this.model.set('captchaImage', imageUtil.getCaptchaPresentationServerURLByView(this));
        }

        undoService.add({
            undo: () =>{
                // Update model
                if (util.isGroupContainer(this.model.get('factoryKey'))){
                    util.updatePropertyRenderAndSendEvent(propName,oldValue,this.model,this.render.bind(this),'GroupPropertyChange');

                }else{
                    this.model.set(propName, oldValue);
                }

                let oldColor = this.resolveColor(oldValue);
                // Update text input
                $('#' + propName).val(oldColor);

            },
            redo:() => {
                // Update model
                if (util.isGroupContainer(this.model.get('factoryKey'))){
                    util.updatePropertyRenderAndSendEvent(propName,color,this.model,this.render.bind(this),'GroupPropertyChange');
                }else{
                    this.model.set(propName, color);
                }

                let newColor = this.resolveColor(propVal);

                // Update text input
                $('#' + propName).val(newColor);
            }
        },textConstants.UNDO_PROP_CHANGE)
    }




    // Helper assumes drop-down ID name same as a property name, and that the property has a model value.
    setDropdownSelection(propName){
        util.selectDropdownItem(propName, this.model.get(propName));
    }

    // Uses "this" from child class to resolve dropdown properties to that it can set their current visible selection
    updateForm( immutablePropNames = this.dropdownNames) {
        this.dropdownPropNames
            .filter(p => this.model.has(p))
            .forEach(p => this.setDropdownSelection(p));
    }
   
    createEditableDropDown(property){
        $(`#${property}`, this.$el).combobox({
            value: this.model.get(property),
            model: this.model,
            controlId:`#${property}`
        });
    }

    checkBorderDropDown(ID, property){

        if(this.model.get(property).toLowerCase() === "none"){
            $(` ${ID} + span.custom-combobox > input`).prop('disabled', true); 
            $(` ${ID} + span.custom-combobox a`).css('pointer-events', 'none');
        }
        else{
            $(` ${ID} + span.custom-combobox > input`).prop('disabled', false); 
            $(` ${ID} + span.custom-combobox a`).css('pointer-events', 'all');
        }          
    }

    addMultilanguageIcon() {

        const self = this;
        if (util.isTranslationEnabled()) {

            const addTranslateIconToInput = ($input) => {
                // Check if the input already has the icon to avoid duplicates
                if ($input.parent().hasClass('input-with-icon')) {
                    return;
                }
                
                const $wrapper = $('<div class="input-with-icon"></div>');
                const $iconSpan = $('<span class="translate-icon" title="Translate this field"></span>');
                const $iconTag = $('<i class="pf-icon-translate"></i>');

                if ($input.is('textarea')) {
                    $wrapper.addClass('textarea-icon-wrapper');
                }

                $iconSpan.append($iconTag);
                $input.wrap($wrapper);
                $input.parent().append($iconSpan);

                $iconTag.on("click", (ev)=>{
                    const attribute = $input.data('attr');
                    let id = "";

                    switch(self.model.get("factoryKey")){
                        case 'layout_frame':
                            id = "app";
                        break;
                        case 'control_table':
                            if(listService.isTableColumnSelected){
                                const index = self.model.get("selectedRowId");
                                id = self.model.get("gridData")[index].get("id");
                            }
                            else{
                                id = self.model.get("id");
                            }
                        break;
                        default:
                            id = self.model.get("id");
                    }
                    
                    util.openMultilangManager(id, attribute);
                });
            }
            const $translatableElements = $('input.translatable, textarea.translatable');

            $translatableElements.each(function () {
                addTranslateIconToInput($(this));
            });
        }
    }


    // Not a typo:
    spectrum;

    render() {
        if(this.$el.html() !== ""){ 
            // try to destroy existing spectrum, maybe it's there
            if(this.spectrum){  
                this.spectrum.spectrum('destroy').off();  
            }  
        }

        this.$el.empty();

        // Delay creation of spectrum, to allow creating of template.
        // This should be reviewed with a replacement for .spectrum()
        setTimeout(()=>{
            if(this.spectrum){
                // Destroy any existing spectrum
                this.spectrum.spectrum('destroy').off();
            }

            // Create spectrum for type=color
            this.spectrum = $('input[type=color]', this.$el).spectrum(pickerCfg);
        }, 50);

        if (listService.loadProperties) {
            setTimeout(() => {
                let propStyles = $("#propStyle");
                let factoryKey;
                if (listService.propertyTab_Access && listService.propertyTab_Access.model) {
                    factoryKey = listService.propertyTab_Access.model.get("factoryKey");
                }
                propStyles.removeClass("propStyleTab")

                if (factoryKey && _.includes(["layout_frame", "reports_frame"], factoryKey)) {
                    if (!propStyles.hasClass("propStyleTab")) {
                        propStyles.addClass("propStyleTab");
                    }

                    let isExpanded = $("#right-rail .rail-header > a.toggle").data("open");
                    let propStyleLabels = this.$(".prop-sublabel");

                    let addStyle = isExpanded ? "prop-sublabel-expanded"
                        : "prop-sublabel-collapsed";

                    let removeClass = isExpanded ? "prop-sublabel-collapsed"
                        : "prop-sublabel-expanded";
                    propStyleLabels
                        .removeClass(removeClass)
                        .addClass(addStyle);
                }
            }, 0);


            eventBus.trigger('gridSizeSet', util.isTrue(userService.userSettings.attributes._edShGrid));
            
            if (listService.mainKind === 'M') {
                $('#property-tabs > .nav-tabs li:nth-child(2)').css('display', '');
                if (this.model.get('factoryKey') === "layout_frame") {
                    $('#property-tabs > .nav-tabs li:nth-child(2)').css('display', 'none');
                }

                const propPanel = $('#property-tabs');
                propPanel.css('marginTop', '');
                if (this.model.get('factoryKey') === "control_section") {
                    propPanel.css('marginTop', '15px');
                }
            }
            listService.loadProperties = false;
        }
    }

    remove() {
        this.undelegateEvents();
        this.stopListening();

        // If we have a spectrum created
        if(this.spectrum){
            // Destroy and unbind
            this.spectrum.spectrum('destroy').off();
        }

        // Unbind any element, child of sp-container, which is created by spectrum
        $('.sp-container *', this.$el).off();

        this.$el.empty();
        
        super.remove();
    }
}

export default BasePropertyView;
